import styled from 'styled-components';
import React from 'react';
import { Box, Flex, Grid, GridItem } from '@lifechurch/react-ion';
import AnalyticsHorizontalStatBox from '../AnalyticsHorizontalStatBox';

import AnalyticsOverviewSalvationsBox from './AnalyticsOverviewSalvationsBox';
import dayjs from 'dayjs';
import AnalyticsOverviewAccountsBox from './AnalyticsOverviewAccountsBox';
import AnalyticsOverviewPrayerBox from './AnalyticsOverviewPrayerBox';
import { ChartPoint } from 'chart.js';

import AnalyticsOverviewAttendanceBox from './AnalyticsOverviewAttendanceBox';
import AnalyticsOverviewTopServiceBox, { TopService } from './AnalyticsOverviewTopServiceBox';
import { mapArrayObjToArrayKeyValues, sum, nFormatter } from '@lib/utils';
import {
  ActiveMetricFieldsFragment,
  AnalyticsOverviewFieldsFragment,
  ComparisonMetricFieldsFragment,
  GroupingUnitType,
  ServicesConnectionFieldsFragment,
} from '@generated/types';
import { useAnalyticsOverviewContext } from '@lib/servicesContext';
import { toMinutesSecondsWithLabel } from '../../Utils';
import { useTranslation } from 'react-i18next';

const StyledFlex = styled(Flex)`
  button {
    margin-inline-start: 2px;
    margin-top: 1px;
    align-self: baseline;
  }
  h2 {
    margin-inline-start: auto;
  }
`;

const StyledHorizontalBorder = styled(Box)`
  width: 100%;
  background-color: ${props => props.theme.colors.gray10};
  margin: 0 auto;
`;

const StyledVerticalBorder = styled(StyledHorizontalBorder)`
  margin: auto;
`;

const hasCompleteRangeData = ({
  metrics,
  unit,
  rangeInDays,
  rangeInWeeks,
  rangeInMonths,
}: {
  metrics: ActiveMetricFieldsFragment[] | ComparisonMetricFieldsFragment[];
  unit: GroupingUnitType;
  rangeInDays: number;
  rangeInWeeks: number;
  rangeInMonths: number;
}): boolean => {
  switch (unit) {
    case 'DAY':
      return metrics.length === rangeInDays;
    case 'WEEK':
      return metrics.length === rangeInWeeks;
    case 'MONTH':
      return metrics.length === rangeInMonths;
    default:
      return true;
  }
};

const completeMetricTimepoints = ({
  metrics,
  unit,
  rangeInDays,
  rangeInWeeks,
  rangeInMonths,
  startTime,
}: {
  metrics: ActiveMetricFieldsFragment[] | ComparisonMetricFieldsFragment[];
  unit: GroupingUnitType;
  rangeInDays: number;
  rangeInWeeks: number;
  rangeInMonths: number;
  startTime: dayjs.Dayjs;
}): dayjs.Dayjs[] => {
  const timepoints: dayjs.Dayjs[] = metrics.map(metric => dayjs(metric?.timepoint));
  let range: number;
  let lowerCasedUnit: 'day' | 'week' | 'month';

  switch (unit) {
    case 'DAY':
      range = rangeInDays;
      lowerCasedUnit = 'day';
      break;
    case 'WEEK':
      range = rangeInWeeks;
      lowerCasedUnit = 'week';
      break;
    case 'MONTH':
      range = rangeInMonths;
      lowerCasedUnit = 'month';
      break;
    default:
      range = rangeInDays;
      lowerCasedUnit = 'day';
      break;
  }

  if (Math.abs(startTime.diff(timepoints[0], lowerCasedUnit)) !== 0) {
    timepoints.splice(0, 0, startTime);
  }

  for (let i = 0, l = range; i < l; i++) {
    const current = timepoints[i];
    const next = timepoints[i + 1];

    if (1 < Math.abs(dayjs(current).diff(next, lowerCasedUnit))) {
      timepoints.splice(i + 1, 0, current.add(1, lowerCasedUnit));
    }
  }

  return timepoints;
};

interface ReturnData {
  dateRange: string[] | undefined;
  rangeInDays: number;
  salvationsTotal: number;
  avgTimeAttendedTotal: number;
  timeAttendedTotal: number;
  avgPrayerMessagesTotal: number;
  prayerRequestersTotal: number;
  activePrayerRequestsTotal: number;
  accountsChangeOverLastRange: number;
  uniqueHostsChangeOverLastRange: number;
  offlineRequestsChangeOverLastRange: number;
  prayerSessionsOverLastRange: number;
  activeAccountsTotal: number;
  uniqueHostsTotal: number;
  offlineRequestsTotal: number;
  activePrayerSessionsTotal: number;
  accountsCreatedMetric: ChartPoint[] | number[];
  prayerSessionsMetric: ChartPoint[] | number[];
  salvationsMetric: ChartPoint[] | number[];
  topAttendedServicesMetrics: TopService[] | undefined;
  attendanceTotal: number;
  uniqueAttendersMetric: ChartPoint[] | number[];
  uniqueAttendersPreviousYearMetric: ChartPoint[] | number[];
  attendanceOverLastRange: number;
}

interface LoadProps {
  analyticsOverview: AnalyticsOverviewFieldsFragment;
  servicesConnection: ServicesConnectionFieldsFragment | null | undefined;
  startTime: dayjs.Dayjs;
  endTime: dayjs.Dayjs;
}

export const loadData = ({
  servicesConnection,
  analyticsOverview,
  startTime,
  endTime,
}: LoadProps): ReturnData => {
  // Service Data
  const topAttendedServicesMetrics = servicesConnection?.services
    .map(service => ({
      uniqueAttenders: service?.metrics[0]?.viewers0s || 0,
      serviceDate: service?.scheduleTime || '',
      serviceId: service?.id || '',
    }))
    .sort((a, b) => b.uniqueAttenders - a.uniqueAttenders)
    .filter((service, idx) => {
      if (idx < 5) return service;
    });
  const dateRangeForCalc = servicesConnection?.services.map(service =>
    dayjs(service?.scheduleTime)
  );
  const rangeInDays = dateRangeForCalc ? endTime.diff(startTime, 'day') : 0;

  const rangeInWeeks = dateRangeForCalc ? endTime.diff(startTime, 'week') : 0;

  const rangeInMonths = dateRangeForCalc ? endTime.diff(startTime, 'month') : 0;

  // Analytics Data
  const activeMetrics = analyticsOverview.activeMetrics;
  const comparisonMetrics = analyticsOverview.comparisonMetrics;
  const groupingUnit = analyticsOverview?.groupingUnit;

  const generateDataRange = (
    unit: GroupingUnitType
  ): { metrics: dayjs.Dayjs[]; compMetrics: dayjs.Dayjs[]; range: string[] } => {
    const isRangeDataComplete = hasCompleteRangeData({
      metrics: activeMetrics,
      unit,
      rangeInDays,
      rangeInWeeks,
      rangeInMonths,
    });

    let metrics: dayjs.Dayjs[];
    let compMetrics: dayjs.Dayjs[];

    let range: string[] | undefined = [];

    if (!isRangeDataComplete) {
      metrics = completeMetricTimepoints({
        metrics: activeMetrics,
        unit,
        rangeInDays,
        rangeInWeeks,
        rangeInMonths,
        startTime,
      });

      compMetrics = completeMetricTimepoints({
        metrics: comparisonMetrics,
        unit,
        rangeInDays,
        rangeInWeeks,
        rangeInMonths,
        startTime,
      });
    } else {
      metrics = activeMetrics.map(metric => dayjs(metric?.timepoint));
      compMetrics = comparisonMetrics.map(metric => dayjs(metric?.timepoint));
    }

    switch (unit) {
      case 'DAY':
        range = metrics.map(date => date.format('MMM D'));
        break;
      case 'WEEK':
        range = metrics.map(date => date.format('Week of MMM D'));
        break;
      case 'MONTH':
        range = metrics.map(date => date.format('MMM'));
        break;
      default:
        range = metrics.map(date => date.format('MMM D'));
        break;
    }
    return { range, metrics, compMetrics };
  };

  const { metrics, compMetrics, range: dateRange } = generateDataRange(groupingUnit);

  // Analytics Metrics:

  // ACTIVE
  const {
    totalTimeAttendedMetric,
    salvationsMetric,
    avgTimeAttendedMetric,
    accountsCreatedMetric,
    avgPrayerMessagesMetric,
    uniqueHostsMetric,
    offlineRequestsMetric,
    prayerRequestersMetric,
    prayerRequestsMetric,
    prayerSessionsMetric,
    uniqueAttendersMetric,
    uniqueAttendersPreviousYearMetric,
  }: {
    [propName: string]: number[];
  } = mapArrayObjToArrayKeyValues(
    activeMetrics,
    [
      'salvations',
      'accountsCreated',
      'avgPrayerMessages',
      'totalTimeAttended',
      'avgTimeAttended',
      'uniqueHosts',
      'offlineRequests',
      'prayerRequesters',
      'prayerRequests',
      'prayerSessions',
      'uniqueAttenders',
      'uniqueAttendersPreviousYear',
    ],
    metrics,
    groupingUnit
  );

  // COMP
  const {
    accountsCreatedMetric: accountsCreatedCompMetric,
    uniqueHostsMetric: uniqueHostsCompMetric,
    offlineRequestsMetric: offlineRequestsCompMetric,
    prayerSessionsMetric: prayerSessionsCompMetric,
    uniqueAttendersMetric: uniqueAttendersCompMetric,
  }: {
    [propName: string]: number[];
  } = mapArrayObjToArrayKeyValues(
    comparisonMetrics,
    ['accountsCreated', 'uniqueHosts', 'offlineRequests', 'prayerSessions', 'uniqueAttenders'],
    compMetrics,
    groupingUnit
  );

  // Analytics: Key Totals
  const avgTimeAttendedTotal: number = Math.round(
    sum(avgTimeAttendedMetric) / avgTimeAttendedMetric.length
  );
  const salvationsTotal: number = sum(salvationsMetric);
  const timeAttendedTotal: number = sum(totalTimeAttendedMetric);
  const activeAccountsTotal: number = sum(accountsCreatedMetric);
  const comparisonAccountsTotal: number = sum(accountsCreatedCompMetric);
  const avgPrayerMessagesTotal: number = sum(avgPrayerMessagesMetric);
  const uniqueHostsTotal: number = sum(uniqueHostsMetric);
  const uniqueHostsCompTotal: number = sum(uniqueHostsCompMetric);
  const offlineRequestsTotal: number = sum(offlineRequestsMetric);
  const offlineRequestsCompTotal: number = sum(offlineRequestsCompMetric);
  const prayerRequestersTotal: number = sum(prayerRequestersMetric);
  const activePrayerRequestsTotal: number = sum(prayerRequestsMetric);
  const activePrayerSessionsTotal: number = sum(prayerSessionsMetric);
  const comparisonPrayerSessionsTotal: number = sum(prayerSessionsCompMetric);
  const attendanceTotal: number = sum(uniqueAttendersMetric);
  const comparisonAttendanceTotal: number = sum(uniqueAttendersCompMetric);

  // Percentage Data Points
  const accountsChangeOverLastRange =
    comparisonAccountsTotal &&
    Math.round(((comparisonAccountsTotal - activeAccountsTotal) / activeAccountsTotal) * 100);

  const uniqueHostsChangeOverLastRange =
    uniqueHostsCompTotal &&
    Math.round(((uniqueHostsCompTotal - uniqueHostsTotal) / uniqueHostsTotal) * 100);

  const offlineRequestsChangeOverLastRange =
    offlineRequestsCompTotal &&
    Math.round(((offlineRequestsCompTotal - offlineRequestsTotal) / offlineRequestsTotal) * 100);

  const prayerSessionsOverLastRange =
    comparisonPrayerSessionsTotal &&
    Math.round(
      ((comparisonPrayerSessionsTotal - activePrayerSessionsTotal) / activePrayerSessionsTotal) *
        100
    );

  const attendanceOverLastRange =
    comparisonAttendanceTotal &&
    Math.round(((comparisonAttendanceTotal - attendanceTotal) / attendanceTotal) * 100);

  return {
    // Date Ranges
    dateRange,
    rangeInDays,
    // MISC Data Points
    avgPrayerMessagesTotal,
    prayerRequestersTotal,
    activePrayerRequestsTotal,
    topAttendedServicesMetrics,
    // Time Attended
    avgTimeAttendedTotal,
    timeAttendedTotal,
    // Salvations
    salvationsTotal,
    salvationsMetric,
    // Accounts
    activeAccountsTotal,
    accountsCreatedMetric,
    accountsChangeOverLastRange,
    // Unique Hosts
    uniqueHostsChangeOverLastRange,
    uniqueHostsTotal,
    // Offline Prayer Requests
    offlineRequestsChangeOverLastRange,
    offlineRequestsTotal,
    // Prayer Sessions
    activePrayerSessionsTotal,
    prayerSessionsMetric,
    prayerSessionsOverLastRange,
    // Attendance
    attendanceTotal,
    uniqueAttendersMetric,
    uniqueAttendersPreviousYearMetric,
    attendanceOverLastRange,
  };
};

interface ChartDataProps {
  dateRange: string[] | undefined;
  metricData: ChartPoint[] | number[];
  label: string;
  grey?: boolean;
}

export const generateChartData = ({
  dateRange,
  metricData,
  label,
  grey,
}: ChartDataProps): Chart.ChartData => {
  const color1 = grey ? '#9F9FA0' : '#F59001';

  return {
    labels: dateRange,
    datasets: [
      {
        backgroundColor: color1,
        data: metricData,
        label,
        lineTension: 0,
        pointBackgroundColor: color1,
        pointBorderWidth: 0,
        pointBorderColor: color1,
        pointRadius: 4,
        pointHoverRadius: 8,
        pointHoverBorderWidth: 0,
        pointHoverBorderColor: color1,
        pointHoverBackgroundColor: color1,
      },
    ],
  };
};

interface Props {
  analyticsOverview: AnalyticsOverviewFieldsFragment;
  servicesConnection: ServicesConnectionFieldsFragment | null | undefined;
}

const AnalyticsOverviewGrid: React.FC<Props> = ({ analyticsOverview, servicesConnection }) => {
  const { from, to } = useAnalyticsOverviewContext();
  const { t } = useTranslation('analytics_overview');
  /*This Function takes in data from query and returns
   consumable data points needed within the Analytics Overview grid */
  const {
    // Date Ranges
    dateRange,
    rangeInDays,
    // Time Attended
    avgTimeAttendedTotal,
    timeAttendedTotal,
    //hostsPresentTotal,
    avgPrayerMessagesTotal,
    prayerRequestersTotal,
    activePrayerRequestsTotal,
    topAttendedServicesMetrics,
    // Salvations
    salvationsTotal,
    salvationsMetric,
    // Accounts
    activeAccountsTotal,
    accountsCreatedMetric,
    accountsChangeOverLastRange,
    // Unique Hosts
    uniqueHostsChangeOverLastRange,
    uniqueHostsTotal,
    // Offline Prayer Requests
    offlineRequestsChangeOverLastRange,
    offlineRequestsTotal,
    // Prayer Sessions
    activePrayerSessionsTotal,
    prayerSessionsMetric,
    prayerSessionsOverLastRange,
    // Attendance
    attendanceTotal,
    attendanceOverLastRange,
    uniqueAttendersMetric,
    uniqueAttendersPreviousYearMetric,
  } = loadData({
    analyticsOverview,
    servicesConnection,
    startTime: dayjs(from),
    endTime: dayjs(to),
  });

  // Chart Data
  const salvationsData: Chart.ChartData = generateChartData({
    dateRange,
    metricData: salvationsMetric,
    label: t('salvations'),
  });

  const accountsData: Chart.ChartData = generateChartData({
    dateRange,
    metricData: accountsCreatedMetric,
    label: t('new_accounts'),
  });

  const livePrayerSessionsData: Chart.ChartData = generateChartData({
    dateRange,
    metricData: prayerSessionsMetric,
    label: t('prayer_sessions'),
  });

  const attendanceData: Chart.ChartData = generateChartData({
    dateRange,
    metricData: uniqueAttendersMetric,
    label: t('this_year'),
  });

  const previousYearData: Chart.ChartData = generateChartData({
    dateRange,
    metricData: uniqueAttendersPreviousYearMetric,
    label: t('previous_year'),
    grey: true,
  });

  const mergedAttendanceData = {
    labels: attendanceData.labels,
    datasets: previousYearData?.datasets?.concat(attendanceData?.datasets ?? []) ?? [],
  };

  return (
    <>
      <Box
        paddingBlockStart={5}
        px={5}
        paddingBlockEnd={5}
        backgroundColor='white'
        borderRadius={2}
        marginBlockStart={5}
        height='384px'
      >
        <AnalyticsOverviewAttendanceBox
          attendance={attendanceTotal.toLocaleString()}
          range={rangeInDays}
          trend={attendanceOverLastRange}
          chartdata={mergedAttendanceData}
          hasComparison={!!attendanceOverLastRange}
        />
      </Box>
      <Grid
        backgroundColor='white'
        borderRadius={2}
        marginBlockStart={5}
        gridTemplateColumns={'1fr 1px 1fr'}
        gridRowGap='1rem'
      >
        <GridItem paddingInlineStart={5} paddingInlineEnd={6} gridColumn='1'>
          <AnalyticsHorizontalStatBox
            title={t('total_time_attended')}
            value={t('minute', { minute: nFormatter(timeAttendedTotal / 60, 1) })}
            tooltip={t('total_time_attended_tooltip')}
            crossPadding={0}
            leftPadding={5}
            rightPadding={6}
          />
        </GridItem>
        <StyledVerticalBorder display={'block'} height='32px' />
        <GridItem paddingInlineEnd={5} paddingInlineStart={6} gridColumn={'3'}>
          <AnalyticsHorizontalStatBox
            title={t('average_time_attended')}
            value={toMinutesSecondsWithLabel(avgTimeAttendedTotal, t)}
            tooltip={t('average_time_attended_tooltip')}
            crossPadding={0}
            leftPadding={5}
            rightPadding={6}
          />
        </GridItem>
      </Grid>
      <Grid
        paddingBlockStart={5}
        gridTemplateColumns={'1fr 1fr'}
        gridColumnGap='1rem'
        gridRowGap='1rem'
      >
        <Box
          paddingBlockStart={5}
          px={5}
          paddingBlockEnd={5}
          backgroundColor='white'
          borderRadius={2}
          height='224px'
        >
          <StyledFlex>
            <AnalyticsOverviewSalvationsBox
              chartdata={salvationsData}
              salvations={salvationsTotal.toLocaleString()}
            />
          </StyledFlex>
        </Box>
        <Box
          paddingBlockStart={5}
          px={3}
          paddingBlockEnd={5}
          backgroundColor='white'
          borderRadius={2}
          height='224px'
        >
          <AnalyticsOverviewTopServiceBox serviceData={topAttendedServicesMetrics} />
        </Box>
      </Grid>
      <Grid
        marginBlockStart={5}
        backgroundColor='white'
        gridTemplateColumns={'1fr 1px 1fr'}
        gridRowGap='1rem'
        borderRadius={2}
      >
        <Box
          paddingBlockStart={5}
          paddingInlineStart={5}
          paddingInlineEnd={6}
          paddingBlockEnd={5}
          height='224px'
        >
          <AnalyticsOverviewPrayerBox
            chartdata={livePrayerSessionsData}
            prayerSessions={activePrayerSessionsTotal.toLocaleString()}
            trend={prayerSessionsOverLastRange}
          />
        </Box>
        <StyledVerticalBorder display={'block'} height='192px' />
        <Box
          paddingBlockStart={5}
          paddingInlineStart={6}
          paddingInlineEnd={5}
          paddingBlockEnd={5}
          height='224px'
        >
          <GridItem gridColumn='1'>
            <AnalyticsHorizontalStatBox
              title={t('prayer_requests')}
              value={activePrayerRequestsTotal.toLocaleString()}
              tooltip={t('prayer_requests_tooltip')}
              crossPadding={0}
              rightPadding={5}
              leftPadding={6}
            />
            <StyledHorizontalBorder height='1px' />
            <GridItem>
              <AnalyticsHorizontalStatBox
                title={t('messages_per_session')}
                value={avgPrayerMessagesTotal.toLocaleString()}
                tooltip={t('messages_per_session_tooltip')}
                crossPadding={0}
                rightPadding={5}
                leftPadding={6}
              />
              <StyledHorizontalBorder height='1px' />
            </GridItem>
            <GridItem>
              <AnalyticsHorizontalStatBox
                title={t('unique_requesters')}
                value={prayerRequestersTotal.toLocaleString()}
                tooltip={t('unique_requesters')}
                crossPadding={0}
                rightPadding={5}
                leftPadding={6}
              />
            </GridItem>
          </GridItem>
        </Box>
      </Grid>
      <Grid
        paddingBlockStart={5}
        gridTemplateColumns={'1fr 1fr'}
        gridColumnGap='1rem'
        gridRowGap='1rem'
      >
        <GridItem gridColumn='1'>
          <GridItem>
            <AnalyticsHorizontalStatBox
              title={t('unique_hosts')}
              value={uniqueHostsTotal.toLocaleString()}
              tooltip={t('unique_hosts_tooltip')}
              trendValue={uniqueHostsChangeOverLastRange}
            />
          </GridItem>
          <GridItem paddingBlockStart={5}>
            <AnalyticsHorizontalStatBox
              title={t('offline_prayer_requests')}
              value={offlineRequestsTotal.toLocaleString()}
              tooltip={t('offline_prayer_requests_tooltip')}
              trendValue={offlineRequestsChangeOverLastRange}
            />
          </GridItem>
        </GridItem>
        <GridItem gridColumn={'2'}>
          <Box
            paddingBlockStart={5}
            px={5}
            paddingBlockEnd={5}
            backgroundColor='white'
            borderRadius={2}
            height='224px'
          >
            <StyledFlex>
              <AnalyticsOverviewAccountsBox
                chartdata={accountsData}
                accounts={activeAccountsTotal.toLocaleString()}
                trend={accountsChangeOverLastRange}
              />
            </StyledFlex>
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};

export default AnalyticsOverviewGrid;
